import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";
import { DATE_FORMAT } from "./date_utils.js";

dayjs.extend(utc);
dayjs.extend(timezone);

const NOTE_DATE_FORMAT = "MMM D, YYYY";

/**
 * The default date range of the notes list: from the first day of the current year to today, in the team time zone.
 * @param {string} timeZone The team time zone, for example "America/New_York".
 * @param {import("dayjs").ConfigType} [now] The current time. It is for tests.
 * @returns {{ from: string, to: string }} Two `YYYY-MM-DD` dates.
 */
export const defaultNotesRange = (timeZone, now) => {
  const today = dayjs(now).tz(timeZone);
  return { from: today.startOf("year").format(DATE_FORMAT), to: today.format(DATE_FORMAT) };
};

/**
 * The date of a note, in the team time zone, for example "Oct 5, 2026".
 * @param {string} createdAt An ISO time in UTC.
 * @param {string} timeZone The team time zone.
 * @returns {string}
 */
export const formatNoteDate = (createdAt, timeZone) => dayjs(createdAt).tz(timeZone).format(NOTE_DATE_FORMAT);

/**
 * @typedef {{ type: "relative", offset_days: number } | { type: "absolute", value: string } | { type: "latest_n", n: number }} DateEdge
 */

/**
 * The two calendar dates that a sandbox date filter spans, in the team time zone.
 * A relative edge counts days from today. An absolute edge is a `YYYY-MM-DD` date.
 * @param {{ start_date: DateEdge, end_date: DateEdge }} dateFilter
 * @param {string} timeZone The team time zone.
 * @param {import("dayjs").ConfigType} [now] The current time. It is for tests.
 * @returns {{ from: string, to: string } | null} Null when an edge is not a calendar date, or when the start is after the end.
 */
export const resolveDateFilterRange = (dateFilter, timeZone, now) => {
  const today = dayjs(now).tz(timeZone);
  const edgeDate = (edge) => {
    if (edge.type === "relative") return today.add(edge.offset_days, "day").format(DATE_FORMAT);
    return edge.type === "absolute" && edge.value ? edge.value : null;
  };
  const from = edgeDate(dateFilter.start_date);
  const to = edgeDate(dateFilter.end_date);
  return from && to && from <= to ? { from, to } : null;
};
